import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, WarningCircle } from "@phosphor-icons/react";
import { useNoIndex, useSoft404 } from "../lib/usePageMeta";

/**
 * SPA fallback for unknown routes. Also used by /checkout (there is no
 * customer-facing checkout page — Stripe hosts the payment flow).
 *
 * ``useSoft404`` emits ``<meta name="prerender-status-code" content="404">``
 * and ``<meta name="robots" content="noindex, nofollow">`` so crawlers
 * recognise the URL as gone even though the initial HTTP response is 200
 * (ingress-controlled).
 */
export default function NotFound() {
  const { pathname } = useLocation();
  const isCheckoutRedirect = pathname === "/checkout";
  const is404 = !isCheckoutRedirect;

  // Two different meta strategies:
  //   * /checkout → real page, noindex
  //   * everything else → real 404, prerender-status-code=404
  useNoIndex(isCheckoutRedirect ? pathname : undefined);
  useSoft404(is404 ? pathname : undefined);

  return (
    <div
      className="mps-container py-24 text-center"
      data-testid={is404 ? "notfound-page" : "checkout-redirect-page"}
      data-http-status={is404 ? "404" : "200"}
    >
      <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-brand-surface border border-brand-border mb-6">
        <WarningCircle size={22} weight="regular" className="text-brand-accent" />
      </div>
      {isCheckoutRedirect ? (
        <>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-brand-navy leading-tight">
            Checkout happens on Stripe
          </h1>
          <p className="mt-4 text-brand-muted max-w-md mx-auto leading-relaxed">
            Add items to your cart and accept the research-use certifications — the checkout button hands off to Stripe. There is no separate on-site checkout page.
          </p>
          <div className="mt-8 flex items-center justify-center gap-3">
            <Link to="/cart" className="inline-flex items-center gap-2 h-12 px-7 rounded-[14px] bg-brand-navy hover:bg-brand-navy2 text-white font-semibold transition-colors">
              Go to cart <ArrowRight size={14} weight="bold" />
            </Link>
            <Link to="/shop" className="inline-flex items-center gap-2 h-12 px-7 rounded-[14px] bg-white border border-brand-border hover:border-brand-navy text-brand-navy font-semibold transition-colors">
              Continue shopping
            </Link>
          </div>
        </>
      ) : (
        <>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-brand-navy leading-tight">
            Page not found
          </h1>
          <p className="mt-4 text-brand-muted max-w-md mx-auto leading-relaxed">
            The URL you tried doesn't match anything in the catalog. If you followed a saved link, the product may have been re-slugged during our compliance sweep — search the catalog to find the current listing.
          </p>
          <div className="mt-8 flex items-center justify-center gap-3">
            <Link to="/shop" className="inline-flex items-center gap-2 h-12 px-7 rounded-[14px] bg-brand-navy hover:bg-brand-navy2 text-white font-semibold transition-colors">
              Browse the catalog <ArrowRight size={14} weight="bold" />
            </Link>
            <Link to="/" className="inline-flex items-center gap-2 h-12 px-7 rounded-[14px] bg-white border border-brand-border hover:border-brand-navy text-brand-navy font-semibold transition-colors">
              Home
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
